import { config } from '@iheartjane/config';
import type { AppMode, ReservationMode, PriceId } from '@iheartjane/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import noop from 'lodash-es/noop';

import { updateLocalCartUuid } from '@jane/kiosk/data-access/lib/localCartUuid/localCartUuid';
import { api } from '@jane/shared/data-access';
import type { Cart, Tags } from '@jane/shared/types';

interface CreateCartParams {
  app_mode?: AppMode;
  app_mode_name?: AppMode;
  external_source?: string;
  headless_checkout_partner_id?: string;
  products: {
    count: number;
    price_id: PriceId;
    product_id: number;
  }[];
  promo_code?: string;
  reservation_mode: ReservationMode;
  store_id: number;
  tags?: Tags;
  user_agent: string;
  user_group_special_id?: number;
}

type CreateCartBodyParams = Pick<CreateCartParams, 'products' | 'store_id'>;

type CreateCartReturnType = { cart: Cart };

export type UseCreateCartProps = {
  onError?: () => unknown;
  onSuccess?: (cart: Cart) => unknown;
};
export const cartUrl = () => `${config.apiPath}/carts`;

const createCart = async (body: CreateCartBodyParams) => {
  const customBody: CreateCartParams = {
    ...body,
    app_mode: 'kiosk',
    reservation_mode: 'kiosk',
    user_agent: window.navigator.userAgent,
  };
  return await api.post<CreateCartReturnType>(cartUrl(), customBody);
};

export const useCreateCart = ({
  onSuccess = noop,
  onError = noop,
}: UseCreateCartProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCart,
    onError,
    onSuccess: async ({ cart }) => {
      const { uuid } = cart;
      updateLocalCartUuid(uuid);
      await queryClient.invalidateQueries({ queryKey: ['cart'] });
      onSuccess(cart);
    },
  });
};
